import { CardConfig } from 'components/DynamicDashboard/interfaces';
import {
  getLDAPTypeReportRequest,
  getNewFrontReportRequest,
  getPartnerProviderTypeReportRequest,
  getSSOReportRequest,
} from 'modules/partners/actions';
import { useDispatch, useSelector } from 'react-redux';
import ApplicationState from 'store/interfaces';
import palette from 'theme/palette';

const DEFAULT_SELECTED_CARDS = [
  'partner_new_front_enabled',
  'ldap_type',
  'provider_type',
];

const usePartnersDashboardSelector = ({
  partners: {
    isLoading,
    LDAPTypeReport,
    isLoadingLDAPTypeReport,
    newFrontReport,
    isLoadingNewFrontReport,
    providerTypeReport,
    isLoadingProviderTypeReport,
    SSOReport,
    isLoadingSSOReport,
  },
}: ApplicationState) => ({
  isLoading:
    isLoading ||
    isLoadingLDAPTypeReport ||
    isLoadingNewFrontReport ||
    isLoadingProviderTypeReport ||
    isLoadingSSOReport,
  LDAPTypeReport,
  newFrontReport,
  providerTypeReport,
  SSOReport,
});

interface Props {
  filters: Record<string, string>;
}

export const usePartnersDashboard = ({ filters }: Props) => {
  const {
    isLoading,
    LDAPTypeReport,
    newFrontReport,
    providerTypeReport,
    SSOReport,
  } = useSelector(usePartnersDashboardSelector);
  const dispatch = useDispatch();

  const cardsConfig: CardConfig[] = [
    {
      key: 'ldap_type',
      title: 'CONNECTION_TYPE_KEY',
      modalTitle: 'PARTNERS_LDAP_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_PARTNERS_LDAP_KEY',
      multiple: true,
      report: LDAPTypeReport,
      getReport: () => dispatch(getLDAPTypeReportRequest({ filters })),
      options: [
        {
          field: 'internal',
          value: 'internal',
          label: 'INTERNAL_KEY',
          color: palette.deepBlue,
        },
        {
          field: 'external',
          value: 'external',
          label: 'EXTERNAL_KEY',
          color: palette.lightBlue,
        },
        {
          field: 'managed',
          value: 'managed',
          label: 'MANAGED_KEY',
          color: palette.digitalBlue,
        },
      ],
    },
    {
      key: 'partner_new_front_enabled',
      title: 'USE_NEW_PORTAL_KEY',
      modalTitle: 'PARTNERS_NEW_FRONT_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_PARTNERS_STATUS_KEY',
      report: newFrontReport,
      getReport: () => dispatch(getNewFrontReportRequest({ filters })),
      options: [
        {
          field: 'activate',
          value: 'true',
          label: 'ACTIVE_KEY',
          color: palette.digitalBlue,
        },
        {
          field: 'deactivate',
          value: 'false',
          label: 'INACTIVE_KEY',
          color: palette.deepBlue,
        },
      ],
    },
    {
      key: 'provider_type',
      title: 'DYNAMIC_FILTER.PROVIDER_TYPE_KEY',
      modalTitle: 'PARTNERS_PROVIDER_TYPE_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_PARTNERS_PROVIDER_KEY',
      multiple: true,
      report: providerTypeReport,
      getReport: () =>
        dispatch(getPartnerProviderTypeReportRequest({ filters })),
      options: [
        {
          field: 'aws',
          value: 'aws',
          label: 'AWS',
          color: palette.deepBlue,
        },
        {
          field: 'oci',
          value: 'oci',
          label: 'OCI',
          color: palette.lightBlue,
        },
        {
          field: 'multicloud',
          value: 'multicloud',
          label: 'Multicloud',
          color: palette.digitalBlue,
        },
      ],
    },
    {
      key: 'allow_sso_configuration',
      title: 'PARTNER_PAGE.SSO_KEY',
      modalTitle: 'PARTNERS_SSO_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_PARTNERS_CONFIG_KEY',
      report: SSOReport,
      getReport: () => dispatch(getSSOReportRequest({ filters })),
      options: [
        {
          field: 'activate',
          value: 'true',
          label: 'ACTIVE_KEY',
          color: palette.digitalBlue,
        },
        {
          field: 'deactivate',
          value: 'false',
          label: 'INACTIVE_KEY',
          color: palette.deepBlue,
        },
      ],
    },
  ];

  return {
    cardsConfig,
    defaultSelectedCards: DEFAULT_SELECTED_CARDS,
    isLoading,
  };
};
